import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { usePublicSettings } from "@/lib/publicSettings";
import { Save, Loader2, Info, Image as ImageIcon, Upload, Plus, Trash2, GripVertical, ExternalLink } from "lucide-react";
import HeroSlidesEditor from "@/pages/admin/HeroSlidesEditor";
import ExperiencesEditor from "@/pages/admin/ExperiencesEditor";
import TrustedWorldwideEditor from "@/pages/admin/TrustedWorldwideEditor";

/**
 * Iter50-53 (Item #12) — Homepage CMS.
 *
 * Manages independent settings blocks live-consumed by Home.jsx:
 *   - homepage_hero          (bg image, headings, description, CTAs, overlay)
 *   - homepage_stats         (company statistics cards)
 *   - homepage_sections      (per-section enable/disable toggles)
 *   - homepage_marketing     (copy for footer/company blurbs)
 *   - homepage_seo           (page title, meta description, keywords, canonical)
 *   - homepage_custom_design (studio pricing block + bullets + CTAs)  ← Iter53
 *   - homepage_testimonials  (client stories carousel content)         ← Iter53
 *
 * Each section owns its own state, load, and save so new tabs can be added
 * without touching existing ones. Section order on the public homepage is
 * FIXED — this CMS does not implement drag-and-drop reordering.
 *
 * All writes go through PUT /api/admin/settings/{section} and are reflected
 * on the public homepage via /api/settings/public.
 */

const TABS = [
  { key: "slides", label: "Hero Slides" },
  { key: "hero", label: "Hero (legacy fallback)" },
  { key: "trusted", label: "Trusted Worldwide" },
  { key: "experiences", label: "Interactive Experiences" },
  { key: "stats", label: "Statistics" },
  { key: "sections", label: "Sections" },
  { key: "custom", label: "Custom Design" },
  { key: "testimonials", label: "Testimonials" },
  { key: "marketing", label: "Marketing & Final CTA" },
  { key: "seo", label: "SEO" },
];

// Iter53 — collision-safe id generator for repeatable rows (stats, tiers,
// testimonials). `Date.now()` alone collides if the user clicks twice in the
// same millisecond.
let _rowSeq = 0;
const nextRowId = (prefix) => `${prefix}-${Date.now().toString(36)}-${(_rowSeq++).toString(36)}`;

// Clamp a number input into [min, max]; falls back to `fallback` if NaN.
const clampNumber = (raw, min, max, fallback) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

const HeroImagePicker = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads?kind=image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Hero image uploaded");
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail || err.message });
    } finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div>
      <Label className="overline text-muted-foreground">Hero background image</Label>
      <div className="mt-2 aspect-[16/9] bg-background border border-border overflow-hidden flex items-center justify-center max-w-2xl" data-testid="hero-bg-preview">
        {value ? (
          <img src={value} alt="hero preview" className="w-full h-full object-cover" />
        ) : (
          <div className="text-muted-foreground text-sm flex flex-col items-center gap-2"><ImageIcon size={28} /><span>No image set — falls back to default.</span></div>
        )}
      </div>
      <div className="mt-3 flex gap-3 items-center">
        <label className="inline-flex items-center gap-2 cursor-pointer bg-primary text-primary-foreground h-10 px-4 hover:bg-primary/90" data-testid="hero-bg-upload">
          {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Upload</>}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
        </label>
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Or paste image URL" className="rounded-none h-10 flex-1" data-testid="hero-bg-url" />
        {value && (
          <Button variant="outline" onClick={() => onChange("")} className="rounded-none h-10" data-testid="hero-bg-clear">Clear</Button>
        )}
      </div>
    </div>
  );
};

const AdminHomepage = () => {
  const { refresh } = usePublicSettings();
  const [tab, setTab] = useState("slides");
  const [hero, setHero] = useState(null);
  const [marketing, setMarketing] = useState(null);
  const [seo, setSeo] = useState(null);
  const [stats, setStats] = useState(null);
  const [sections, setSections] = useState(null);
  const [custom, setCustom] = useState(null);
  const [testimonials, setTestimonials] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings/all");
      setHero(data.homepage_hero || {});
      setMarketing(data.homepage_marketing || {});
      setSeo(data.homepage_seo || {});
      setStats(data.homepage_stats || { stats: [] });
      setSections(data.homepage_sections || {});
      setCustom(data.homepage_custom_design || { bullets: [], tiers: [] });
      setTestimonials(data.homepage_testimonials || { items: [] });
    } catch (e) { toast.error("Failed to load homepage settings", { description: e.response?.data?.detail || "" }); }
  };
  useEffect(() => { load(); }, []);

  const saveSection = async (section, payload) => {
    setSaving(true);
    try {
      await api.put(`/admin/settings/${section}`, { data: payload });
      toast.success("Saved");
      try { await refresh?.(); } catch (err) { console.debug("[homepage] refresh after save failed", err); }
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (!hero || !marketing || !seo || !stats || !sections || !custom || !testimonials) return <AdminLayout title="Homepage Management"><div className="text-muted-foreground">Loading…</div></AdminLayout>;

  const setH = (patch) => setHero((s) => ({ ...s, ...patch }));
  const setM = (patch) => setMarketing((s) => ({ ...s, ...patch }));
  const setS = (patch) => setSeo((s) => ({ ...s, ...patch }));
  const setC = (patch) => setCustom((s) => ({ ...s, ...patch }));
  const setT = (patch) => setTestimonials((s) => ({ ...s, ...patch }));

  return (
    <AdminLayout title="Homepage Management">
      <div className="border border-border bg-accent/5 p-4 mb-6 flex items-start gap-3" data-testid="homepage-cms-help">
        <Info size={16} className="text-accent shrink-0 mt-0.5" />
        <div className="flex-1 text-sm text-muted-foreground">
          <p>
            Everything you edit here is applied to the public homepage instantly.
            Section order is fixed (Hero → Statistics → Browse by Style → Featured Plans → Why Choose → Custom Design → Testimonials → Final CTA).
            Toggle sections on/off from the Sections tab.
          </p>
          <p className="mt-2 text-xs italic opacity-80">
            Draft / Preview-before-publish is planned for a future release. For now, edits go live immediately — use "View Live Homepage" to verify.
          </p>
        </div>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="homepage-preview-btn"
          className="inline-flex items-center gap-2 h-9 px-3 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90 transition-fast shrink-0"
        >
          View Live Homepage <ExternalLink size={12} />
        </a>
      </div>

      <div className="flex gap-2 mb-6 border-b border-border" data-testid="homepage-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${tab === t.key ? "border-accent text-accent" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "slides" && (
        <HeroSlidesEditor />
      )}

      {tab === "experiences" && (
        <ExperiencesEditor />
      )}

      {tab === "trusted" && (
        <TrustedWorldwideEditor />
      )}

      {tab === "hero" && (
        <div className="max-w-3xl space-y-6" data-testid="hero-section-editor">
          <HeroImagePicker value={hero.background_image_url} onChange={(v) => setH({ background_image_url: v })} />
          <div><Label className="overline text-muted-foreground">Eyebrow</Label>
            <Input value={hero.eyebrow || ""} onChange={(e) => setH({ eyebrow: e.target.value })} className="rounded-none mt-2 h-11" placeholder="Since 2012 · Premium plans · 40+ countries" data-testid="hero-eyebrow-input" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="overline text-muted-foreground">Heading line 1</Label><Input value={hero.heading_line1 || ""} onChange={(e) => setH({ heading_line1: e.target.value })} className="rounded-none mt-2 h-11" data-testid="hero-line1" /></div>
            <div><Label className="overline text-muted-foreground">Heading line 2 (italic)</Label><Input value={hero.heading_line2_italic || ""} onChange={(e) => setH({ heading_line2_italic: e.target.value })} className="rounded-none mt-2 h-11" data-testid="hero-line2" /></div>
            <div><Label className="overline text-muted-foreground">Heading line 3</Label><Input value={hero.heading_line3 || ""} onChange={(e) => setH({ heading_line3: e.target.value })} className="rounded-none mt-2 h-11" data-testid="hero-line3" /></div>
          </div>
          <div><Label className="overline text-muted-foreground">Description</Label>
            <Textarea rows={4} value={hero.description || ""} onChange={(e) => setH({ description: e.target.value })} className="rounded-none mt-2" data-testid="hero-description" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="overline text-muted-foreground">Primary CTA — label</Label><Input value={hero.primary_cta_label || ""} onChange={(e) => setH({ primary_cta_label: e.target.value })} className="rounded-none mt-2 h-11" data-testid="hero-primary-label" /></div>
            <div><Label className="overline text-muted-foreground">Primary CTA — link</Label><Input value={hero.primary_cta_href || ""} onChange={(e) => setH({ primary_cta_href: e.target.value })} className="rounded-none mt-2 h-11" placeholder="/plans" data-testid="hero-primary-href" /></div>
            <div><Label className="overline text-muted-foreground">Secondary CTA — label</Label><Input value={hero.secondary_cta_label || ""} onChange={(e) => setH({ secondary_cta_label: e.target.value })} className="rounded-none mt-2 h-11" data-testid="hero-secondary-label" /></div>
            <div><Label className="overline text-muted-foreground">Secondary CTA — link</Label><Input value={hero.secondary_cta_href || ""} onChange={(e) => setH({ secondary_cta_href: e.target.value })} className="rounded-none mt-2 h-11" placeholder="/custom-design" data-testid="hero-secondary-href" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="overline text-muted-foreground">Overlay opacity (0-1)</Label><Input type="number" step="0.05" min="0" max="1" value={hero.overlay_opacity ?? 0.55} onChange={(e) => setH({ overlay_opacity: clampNumber(e.target.value, 0, 1, 0.55) })} className="rounded-none mt-2 h-11" data-testid="hero-overlay" /></div>
            <div><Label className="overline text-muted-foreground">Featured chip title</Label><Input value={hero.featured_chip_title || ""} onChange={(e) => setH({ featured_chip_title: e.target.value })} className="rounded-none mt-2 h-11" data-testid="hero-chip-title" /></div>
            <div><Label className="overline text-muted-foreground">Featured chip subtitle</Label><Input value={hero.featured_chip_subtitle || ""} onChange={(e) => setH({ featured_chip_subtitle: e.target.value })} className="rounded-none mt-2 h-11" data-testid="hero-chip-subtitle" /></div>
          </div>
          <Button onClick={() => saveSection("homepage_hero", hero)} disabled={saving} className="rounded-none bg-primary h-11 px-8" data-testid="hero-save">
            {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save Hero</>}
          </Button>
        </div>
      )}

      {tab === "stats" && (
        <div className="max-w-3xl space-y-6" data-testid="stats-editor">
          <div>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display text-2xl">Company Statistics</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Cards displayed in the statistics band on the homepage. Set <code className="text-xs bg-muted px-1">value</code> to <code className="text-xs bg-muted px-1">auto</code> for the House-Plans card to render the live catalog count.
                </p>
              </div>
              <Button
                onClick={() => setStats((s) => ({
                  ...s,
                  stats: [...(s.stats || []), { id: nextRowId("stat"), label: "New Stat", value: "0", suffix: "+", italic: false, enabled: true }],
                }))}
                variant="outline"
                className="rounded-none h-10"
                data-testid="stat-add-btn"
              ><Plus size={14} className="mr-2" /> Add stat</Button>
            </div>
          </div>
          <div className="space-y-3">
            {(stats.stats || []).map((row, idx) => (
              <div key={row.id || idx} className="border border-border p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start" data-testid={`stat-row-${idx}`}>
                <div className="md:col-span-1 flex items-center justify-center pt-6 text-muted-foreground">
                  <GripVertical size={16} />
                </div>
                <div className="md:col-span-3">
                  <Label className="overline text-muted-foreground text-xs">Label</Label>
                  <Input value={row.label || ""} onChange={(e) => setStats((s) => ({ ...s, stats: s.stats.map((r, i) => i === idx ? { ...r, label: e.target.value } : r) }))} className="rounded-none mt-1 h-10" data-testid={`stat-label-${idx}`} />
                </div>
                <div className="md:col-span-3">
                  <Label className="overline text-muted-foreground text-xs">Value</Label>
                  <Input value={row.value || ""} onChange={(e) => setStats((s) => ({ ...s, stats: s.stats.map((r, i) => i === idx ? { ...r, value: e.target.value } : r) }))} className="rounded-none mt-1 h-10" placeholder="13 or auto or Global" data-testid={`stat-value-${idx}`} />
                </div>
                <div className="md:col-span-1">
                  <Label className="overline text-muted-foreground text-xs">Suffix</Label>
                  <Input value={row.suffix || ""} onChange={(e) => setStats((s) => ({ ...s, stats: s.stats.map((r, i) => i === idx ? { ...r, suffix: e.target.value } : r) }))} className="rounded-none mt-1 h-10" placeholder="+" data-testid={`stat-suffix-${idx}`} />
                </div>
                <div className="md:col-span-2 flex items-center gap-2 pt-6">
                  <Switch checked={!!row.italic} onCheckedChange={(v) => setStats((s) => ({ ...s, stats: s.stats.map((r, i) => i === idx ? { ...r, italic: v } : r) }))} data-testid={`stat-italic-${idx}`} />
                  <span className="text-xs text-muted-foreground">Italic</span>
                </div>
                <div className="md:col-span-1 flex items-center gap-2 pt-6">
                  <Switch checked={row.enabled !== false} onCheckedChange={(v) => setStats((s) => ({ ...s, stats: s.stats.map((r, i) => i === idx ? { ...r, enabled: v } : r) }))} data-testid={`stat-enabled-${idx}`} />
                </div>
                <div className="md:col-span-1 flex items-center justify-end pt-5">
                  <Button
                    variant="ghost"
                    onClick={() => setStats((s) => ({ ...s, stats: s.stats.filter((_, i) => i !== idx) }))}
                    className="rounded-none h-9 text-destructive hover:text-destructive"
                    data-testid={`stat-delete-${idx}`}
                  ><Trash2 size={14} /></Button>
                </div>
              </div>
            ))}
            {(stats.stats || []).length === 0 && (
              <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                No stats yet. Click "Add stat" to create the first card.
              </div>
            )}
          </div>
          <Button onClick={() => saveSection("homepage_stats", stats)} disabled={saving} className="rounded-none bg-primary h-11 px-8" data-testid="stats-save">
            {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save Statistics</>}
          </Button>
        </div>
      )}

      {tab === "sections" && (
        <div className="max-w-3xl space-y-4" data-testid="sections-editor">
          <div>
            <h3 className="font-display text-2xl">Homepage Section Visibility</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Toggle sections on or off. The visual order on the public homepage is fixed and cannot be rearranged from here.
            </p>
          </div>
          <div className="border border-border divide-y divide-border">
            {Object.entries(sections).map(([key, cfg], idx) => (
              <div key={key} className="p-4 flex items-center justify-between gap-4" data-testid={`section-row-${key}`}>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-muted-foreground w-6 tabular-nums">{String(idx + 1).padStart(2, "0")}</span>
                  <div>
                    <div className="font-medium text-sm">{cfg.label || key}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">{key}</div>
                  </div>
                </div>
                <Switch
                  checked={cfg.enabled !== false}
                  onCheckedChange={(v) => setSections((s) => ({ ...s, [key]: { ...s[key], enabled: v } }))}
                  data-testid={`section-toggle-${key}`}
                />
              </div>
            ))}
          </div>
          <Button onClick={() => saveSection("homepage_sections", sections)} disabled={saving} className="rounded-none bg-primary h-11 px-8" data-testid="sections-save">
            {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save Section Visibility</>}
          </Button>
        </div>
      )}

      {tab === "custom" && (
        <div className="max-w-3xl space-y-6" data-testid="custom-editor">
          <div>
            <h3 className="font-display text-2xl">Custom Design Studio</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Full block content, bullets, CTAs, and pricing tiers. Add or remove tiers freely — the layout adapts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="overline text-muted-foreground">Eyebrow</Label>
              <Input value={custom.eyebrow || ""} onChange={(e) => setC({ eyebrow: e.target.value })} className="rounded-none mt-2 h-11" data-testid="custom-eyebrow" />
            </div>
            <div />
            <div><Label className="overline text-muted-foreground">Heading line 1</Label>
              <Input value={custom.heading_line1 || ""} onChange={(e) => setC({ heading_line1: e.target.value })} className="rounded-none mt-2 h-11" data-testid="custom-heading-line1" />
            </div>
            <div><Label className="overline text-muted-foreground">Heading (italic)</Label>
              <Input value={custom.heading_italic || ""} onChange={(e) => setC({ heading_italic: e.target.value })} className="rounded-none mt-2 h-11" data-testid="custom-heading-italic" />
            </div>
          </div>

          <div><Label className="overline text-muted-foreground">Description</Label>
            <Textarea rows={3} value={custom.description || ""} onChange={(e) => setC({ description: e.target.value })} className="rounded-none mt-2" data-testid="custom-description" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="overline text-muted-foreground">Bullet points</Label>
              <Button
                variant="outline"
                onClick={() => setC({ bullets: [...(custom.bullets || []), "New bullet"] })}
                className="rounded-none h-9"
                data-testid="custom-bullet-add"
              ><Plus size={14} className="mr-2" />Add bullet</Button>
            </div>
            <div className="space-y-2">
              {(custom.bullets || []).map((line, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`custom-bullet-row-${idx}`}>
                  <Input value={line} onChange={(e) => setC({ bullets: custom.bullets.map((b, i) => i === idx ? e.target.value : b) })} className="rounded-none h-10 flex-1" data-testid={`custom-bullet-${idx}`} />
                  <Button variant="ghost" onClick={() => setC({ bullets: custom.bullets.filter((_, i) => i !== idx) })} className="rounded-none h-9 text-destructive" data-testid={`custom-bullet-delete-${idx}`}><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="overline text-muted-foreground">Primary CTA — label</Label><Input value={custom.primary_cta_label || ""} onChange={(e) => setC({ primary_cta_label: e.target.value })} className="rounded-none mt-2 h-11" data-testid="custom-primary-label" /></div>
            <div><Label className="overline text-muted-foreground">Primary CTA — link</Label><Input value={custom.primary_cta_href || ""} onChange={(e) => setC({ primary_cta_href: e.target.value })} className="rounded-none mt-2 h-11" data-testid="custom-primary-href" /></div>
            <div><Label className="overline text-muted-foreground">Secondary CTA — label</Label><Input value={custom.secondary_cta_label || ""} onChange={(e) => setC({ secondary_cta_label: e.target.value })} className="rounded-none mt-2 h-11" data-testid="custom-secondary-label" /></div>
            <div><Label className="overline text-muted-foreground">Secondary CTA — link</Label><Input value={custom.secondary_cta_href || ""} onChange={(e) => setC({ secondary_cta_href: e.target.value })} className="rounded-none mt-2 h-11" data-testid="custom-secondary-href" /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label className="overline text-muted-foreground">Pricing tiers</Label>
                <p className="text-xs text-muted-foreground mt-1">Each row is one tier in the pricing panel.</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setC({ tiers: [...(custom.tiers || []), { label: "New tier", subtitle: "", price: "$0" }] })}
                className="rounded-none h-9"
                data-testid="custom-tier-add"
              ><Plus size={14} className="mr-2" />Add tier</Button>
            </div>
            <div className="space-y-3">
              {(custom.tiers || []).map((tier, idx) => (
                <div key={idx} className="border border-border p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-start" data-testid={`custom-tier-row-${idx}`}>
                  <div className="md:col-span-4">
                    <Label className="overline text-muted-foreground text-xs">Label</Label>
                    <Input value={tier.label || ""} onChange={(e) => setC({ tiers: custom.tiers.map((t, i) => i === idx ? { ...t, label: e.target.value } : t) })} className="rounded-none mt-1 h-10" data-testid={`custom-tier-label-${idx}`} />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="overline text-muted-foreground text-xs">Subtitle</Label>
                    <Input value={tier.subtitle || ""} onChange={(e) => setC({ tiers: custom.tiers.map((t, i) => i === idx ? { ...t, subtitle: e.target.value } : t) })} className="rounded-none mt-1 h-10" data-testid={`custom-tier-subtitle-${idx}`} />
                  </div>
                  <div className="md:col-span-3">
                    <Label className="overline text-muted-foreground text-xs">Price</Label>
                    <Input value={tier.price || ""} onChange={(e) => setC({ tiers: custom.tiers.map((t, i) => i === idx ? { ...t, price: e.target.value } : t) })} className="rounded-none mt-1 h-10" data-testid={`custom-tier-price-${idx}`} />
                  </div>
                  <div className="md:col-span-1 flex items-center justify-end pt-5">
                    <Button variant="ghost" onClick={() => setC({ tiers: custom.tiers.filter((_, i) => i !== idx) })} className="rounded-none h-9 text-destructive" data-testid={`custom-tier-delete-${idx}`}><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="overline text-muted-foreground">Pricing headline</Label><Input value={custom.pricing_headline || ""} onChange={(e) => setC({ pricing_headline: e.target.value })} className="rounded-none mt-2 h-11" data-testid="custom-pricing-headline" /></div>
            <div><Label className="overline text-muted-foreground">Pricing footer</Label><Textarea rows={2} value={custom.pricing_footer || ""} onChange={(e) => setC({ pricing_footer: e.target.value })} className="rounded-none mt-2" data-testid="custom-pricing-footer" /></div>
          </div>

          <Button onClick={() => saveSection("homepage_custom_design", custom)} disabled={saving} className="rounded-none bg-primary h-11 px-8" data-testid="custom-save">
            {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save Custom Design</>}
          </Button>
        </div>
      )}

      {tab === "testimonials" && (
        <div className="max-w-3xl space-y-6" data-testid="testimonials-editor">
          <div>
            <h3 className="font-display text-2xl">Testimonials</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Client stories shown in the homepage carousel. Order in the list = display order.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="overline text-muted-foreground">Eyebrow</Label>
              <Input value={testimonials.eyebrow || ""} onChange={(e) => setT({ eyebrow: e.target.value })} className="rounded-none mt-2 h-11" data-testid="tst-eyebrow" />
            </div>
            <div><Label className="overline text-muted-foreground">Heading line 1</Label>
              <Input value={testimonials.heading_line1 || ""} onChange={(e) => setT({ heading_line1: e.target.value })} className="rounded-none mt-2 h-11" data-testid="tst-heading-line1" />
            </div>
            <div><Label className="overline text-muted-foreground">Heading (italic)</Label>
              <Input value={testimonials.heading_italic || ""} onChange={(e) => setT({ heading_italic: e.target.value })} className="rounded-none mt-2 h-11" data-testid="tst-heading-italic" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="overline text-muted-foreground">Quotes</Label>
              <Button
                variant="outline"
                onClick={() => setT({ items: [...(testimonials.items || []), { quote: "", name: "", role: "" }] })}
                className="rounded-none h-9"
                data-testid="tst-add"
              ><Plus size={14} className="mr-2" />Add quote</Button>
            </div>
            <div className="space-y-3">
              {(testimonials.items || []).map((item, idx) => (
                <div key={idx} className="border border-border p-4 space-y-3" data-testid={`tst-row-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground tabular-nums">Quote #{idx + 1}</div>
                    <Button variant="ghost" onClick={() => setT({ items: testimonials.items.filter((_, i) => i !== idx) })} className="rounded-none h-8 text-destructive" data-testid={`tst-delete-${idx}`}><Trash2 size={14} /></Button>
                  </div>
                  <div>
                    <Label className="overline text-muted-foreground text-xs">Quote text</Label>
                    <Textarea rows={4} value={item.quote || ""} onChange={(e) => setT({ items: testimonials.items.map((r, i) => i === idx ? { ...r, quote: e.target.value } : r) })} className="rounded-none mt-1" data-testid={`tst-quote-${idx}`} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="overline text-muted-foreground text-xs">Name</Label>
                      <Input value={item.name || ""} onChange={(e) => setT({ items: testimonials.items.map((r, i) => i === idx ? { ...r, name: e.target.value } : r) })} className="rounded-none mt-1 h-10" data-testid={`tst-name-${idx}`} />
                    </div>
                    <div>
                      <Label className="overline text-muted-foreground text-xs">Role / Location</Label>
                      <Input value={item.role || ""} onChange={(e) => setT({ items: testimonials.items.map((r, i) => i === idx ? { ...r, role: e.target.value } : r) })} className="rounded-none mt-1 h-10" data-testid={`tst-role-${idx}`} />
                    </div>
                  </div>
                </div>
              ))}
              {(testimonials.items || []).length === 0 && (
                <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  No testimonials yet — click "Add quote" to create the first one.
                </div>
              )}
            </div>
          </div>

          <Button onClick={() => saveSection("homepage_testimonials", testimonials)} disabled={saving} className="rounded-none bg-primary h-11 px-8" data-testid="tst-save">
            {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save Testimonials</>}
          </Button>
        </div>
      )}

      {tab === "marketing" && (
        <div className="max-w-3xl space-y-6" data-testid="marketing-editor">
          <div>
            <h3 className="font-display text-2xl">Marketing Content</h3>
            <p className="text-sm text-muted-foreground mt-1">Company description + Final CTA copy shown at the bottom of the homepage.</p>
          </div>

          <div><Label className="overline text-muted-foreground">Company description</Label>
            <Textarea rows={3} value={marketing.company_description || ""} onChange={(e) => setM({ company_description: e.target.value })} className="rounded-none mt-2" data-testid="marketing-company-description" />
          </div>
          <div><Label className="overline text-muted-foreground">Footer copyright</Label>
            <Input value={marketing.footer_copyright || ""} onChange={(e) => setM({ footer_copyright: e.target.value })} className="rounded-none mt-2 h-11" placeholder="© 2026 Dream Home Engineers · All rights reserved." data-testid="marketing-footer-copyright" />
          </div>

          <div className="border-t border-border pt-6">
            <div className="overline text-accent mb-4">Final CTA (bottom of homepage)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label className="overline text-muted-foreground">Eyebrow</Label>
                <Input value={marketing.final_cta_eyebrow || ""} onChange={(e) => setM({ final_cta_eyebrow: e.target.value })} className="rounded-none mt-2 h-11" data-testid="marketing-fcta-eyebrow" />
              </div>
              <div><Label className="overline text-muted-foreground">Heading line 1</Label>
                <Input value={marketing.final_cta_heading_line1 || ""} onChange={(e) => setM({ final_cta_heading_line1: e.target.value })} className="rounded-none mt-2 h-11" data-testid="marketing-fcta-line1" />
              </div>
              <div><Label className="overline text-muted-foreground">Heading (italic)</Label>
                <Input value={marketing.final_cta_heading_italic || ""} onChange={(e) => setM({ final_cta_heading_italic: e.target.value })} className="rounded-none mt-2 h-11" data-testid="marketing-fcta-italic" />
              </div>
            </div>
            <div className="mt-3"><Label className="overline text-muted-foreground">Description</Label>
              <Textarea rows={2} value={marketing.final_cta_description || ""} onChange={(e) => setM({ final_cta_description: e.target.value })} className="rounded-none mt-2" data-testid="marketing-fcta-description" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div><Label className="overline text-muted-foreground">Primary CTA — label</Label><Input value={marketing.final_cta_primary_label || ""} onChange={(e) => setM({ final_cta_primary_label: e.target.value })} className="rounded-none mt-2 h-11" data-testid="marketing-fcta-primary-label" /></div>
              <div><Label className="overline text-muted-foreground">Primary CTA — link</Label><Input value={marketing.final_cta_primary_href || ""} onChange={(e) => setM({ final_cta_primary_href: e.target.value })} className="rounded-none mt-2 h-11" data-testid="marketing-fcta-primary-href" /></div>
              <div><Label className="overline text-muted-foreground">Secondary CTA — label</Label><Input value={marketing.final_cta_secondary_label || ""} onChange={(e) => setM({ final_cta_secondary_label: e.target.value })} className="rounded-none mt-2 h-11" data-testid="marketing-fcta-secondary-label" /></div>
              <div><Label className="overline text-muted-foreground">Secondary CTA — link</Label><Input value={marketing.final_cta_secondary_href || ""} onChange={(e) => setM({ final_cta_secondary_href: e.target.value })} className="rounded-none mt-2 h-11" data-testid="marketing-fcta-secondary-href" /></div>
            </div>
          </div>

          <Button onClick={() => saveSection("homepage_marketing", marketing)} disabled={saving} className="rounded-none bg-primary h-11 px-8" data-testid="marketing-save">
            {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save Marketing</>}
          </Button>
        </div>
      )}

      {tab === "seo" && (
        <div className="max-w-3xl space-y-6" data-testid="seo-editor">
          <div><Label className="overline text-muted-foreground">Page title</Label>
            <Input value={seo.page_title || ""} onChange={(e) => setS({ page_title: e.target.value })} className="rounded-none mt-2 h-11" data-testid="seo-title" />
            <p className="text-xs text-muted-foreground mt-1">Recommended 55-60 characters.</p>
          </div>
          <div><Label className="overline text-muted-foreground">Meta description</Label>
            <Textarea rows={3} value={seo.meta_description || ""} onChange={(e) => setS({ meta_description: e.target.value })} className="rounded-none mt-2" data-testid="seo-description" />
            <p className="text-xs text-muted-foreground mt-1">Recommended 150-160 characters.</p>
          </div>
          <div><Label className="overline text-muted-foreground">Meta keywords (comma-separated)</Label>
            <Input
              value={(seo.meta_keywords || []).join(", ")}
              onChange={(e) => setS({ meta_keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              className="rounded-none mt-2 h-11"
              data-testid="seo-keywords"
            />
          </div>
          <div><Label className="overline text-muted-foreground">Canonical URL</Label>
            <Input value={seo.canonical_url || ""} onChange={(e) => setS({ canonical_url: e.target.value })} className="rounded-none mt-2 h-11" placeholder="https://plans.dreamhomeengineers.com/" data-testid="seo-canonical" />
          </div>
          <Button onClick={() => saveSection("homepage_seo", seo)} disabled={saving} className="rounded-none bg-primary h-11 px-8" data-testid="seo-save">
            {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save SEO</>}
          </Button>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminHomepage;
